@if (state$ | async; as state) {
  <div class="page-header">
    <div class="page-title">{{ state.orderId === 'new' ? 'Nowe zlecenie' : `Zlecenie ${state.order?.orderNumber ?? ''}` }}</div>
  </div>

  <div class="card" id="data-card">
    <div class="card-header">
      DANE
    </div>

    @if (state.loading) {
      <!-- the six labels are the card's layout; a shadow stands where each value will, and the bar's track without its fill (0127) -->
      <div class="field">
        <div class="field-label">Nazwa</div>
        <div class="field-value"><span class="shadow shadow-long"></span></div>
      </div>

      <div class="field">
        <div class="field-label">Status</div>
        <div class="field-value"><span class="shadow shadow-short"></span></div>
      </div>

      <div class="field">
        <div class="field-label">Zaplanowano</div>
        <div class="field-value"><span class="shadow"></span></div>
      </div>

      <div class="field">
        <div class="field-label">Termin</div>
        <div class="field-value"><span class="shadow shadow-short"></span></div>
      </div>

      <div class="field">
        <div class="field-label">Postęp</div>
        <div class="field-value"><span class="shadow shadow-short"></span></div>
        <div class="order-progress-bar"></div>
      </div>

      <div class="field">
        <div class="field-label">Utworzono</div>
        <div class="field-value"><span class="shadow shadow-short"></span></div>
      </div>
    } @else if (state.orderId === 'new') {
      <div class="data-hint">Zlecenie i jego numer powstaną wraz z pierwszą pozycją.</div>
    } @else if (state.order; as order) {
      <div class="field" (dblclick)="startEditing('name')" title="Dwuklik edytuje">
        <div class="field-label">Nazwa</div>
        @if (editingField() === 'name') {
          <input #fieldInput class="field-input" [value]="order.name"
            (keyup.enter)="saveField('name', fieldInput.value, order)" (keyup.escape)="stopEditing()" (blur)="stopEditing()">
        } @else {
          <div class="field-value">{{ order.name }}</div>
        }
      </div>

      <div class="field">
        <div class="field-label">Status</div>
        <div class="field-value">{{ order.statusLabel }}</div>
      </div>

      <div class="field">
        <div class="field-label">Zaplanowano</div>
        <div class="field-value text-mono" [class.empty]="!order.plannedLabel">{{ order.plannedLabel ?? 'nie zaplanowano' }}</div>
      </div>

      <div class="field" (dblclick)="startEditing('deadline')" title="Dwuklik edytuje">
        <div class="field-label">Termin</div>
        @if (editingField() === 'deadline') {
          <input #fieldInput class="field-input" type="date" [value]="order.deadline ?? ''"
            (change)="saveField('deadline', fieldInput.value, order)" (keyup.escape)="stopEditing()" (blur)="stopEditing()">
        } @else {
          <div class="field-value text-mono" [class.empty]="!order.deadline">{{ order.deadline ? formatDayMonthYear(order.deadline) : '—' }}</div>
        }
      </div>

      <div class="field">
        <div class="field-label">Postęp</div>
        <div class="field-value text-mono">{{ formatPercent(order.progressFraction) }}</div>
        <div class="order-progress-bar">
          <div class="order-progress-fill" [style.width.%]="order.progressFraction * 100"></div>
        </div>
      </div>

      <div class="field">
        <div class="field-label">Utworzono</div>
        <div class="field-value text-mono">{{ formatDayMonthYear(order.createdAt.slice(0, 10)) }}</div>
      </div>
    }
  </div>

  <div class="card" id="notes-card">
    <div class="card-header">
      NOTATKI

      @if (notesOrderId(state) !== undefined) {
        <div class="card-header-toolbar">
          <div class="card-header-icon" (click)="notes()?.toggleComposer()">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
          </div>
        </div>
      }
    </div>

    @if (state.loading) {
      <!-- the zlecenie's id arrives with the zlecenie, so the page draws the two notes until then (0127) -->
      <div class="shadow-note">
        <div class="shadow-note-header"><span class="shadow shadow-short"></span></div>
        <span class="shadow shadow-long"></span>
      </div>
      <div class="shadow-note">
        <div class="shadow-note-header"><span class="shadow shadow-short"></span></div>
        <span class="shadow shadow-long"></span>
      </div>
    } @else {
      <order-notes [orderId]="notesOrderId(state)"></order-notes>
    }
  </div>

  <div class="card" id="items-card">
    <div class="card-header">
      POZYCJE
    </div>

    @if (state.loading) {
      <div class="order-items">
        <!-- three rows of 0097's compact row, at its height: the 40px photo slot holds it (0127) -->
        @for (row of shadowRows; track row) {
          <div class="order-item">
            <div class="order-item-index"><span class="shadow shadow-full"></span></div>
            <div class="shadow shadow-photo-card"></div>
            <div class="order-item-name"><span class="shadow" [class.shadow-long]="$even"></span></div>
            <div class="order-item-parts"><span class="shadow"></span></div>
            <div class="order-item-amount"><span class="shadow"></span></div>
            <span></span>
          </div>
        }
      </div>
    } @else {
      <div class="order-items">
        @for (item of state.order?.items ?? []; track item.id) {
          <!-- a click scopes PODSUMOWANIE to the row, a second click returns to the whole order (0097; the BOM tree's selection) -->
          <div class="order-item" [class.selected]="item.id === state.selectedItemId" (click)="toggleSelectedItem(item)"
            [title]="item.data.clientOrder ? 'Pozycja realizuje zamówienie — odepnij ją w zamówieniu, aby usunąć' : ''">
            <div class="order-item-index">
              {{ $index + 1 }}
            </div>
            <item-photo class="order-item-photo" size="card" [attachmentId]="item.data.primaryPhotoAttachmentId" [label]="item.data.name"></item-photo>
            <div class="order-item-name">
              <div class="order-item-name-text" [title]="item.data.name">{{ item.data.name }}</div>
              @if (item.data.clientOrder; as clientOrder) {
                <a class="order-item-client-order" [routerLink]="['/client-orders', clientOrder.clientOrderId]" title="Pozycja zamówienia, którą ta pozycja realizuje">
                  <span class="text-mono">· {{ clientOrder.orderNumber }}</span> · {{ clientOrder.clientName }}
                </a>
              }
            </div>
            <div class="order-item-parts text-mono" title="Elementów w drzewie pozycji">{{ partCount(item) }} elem.</div>
            <!-- the double-click is two clicks — without the stop they would select and deselect the row on the way to the edit -->
            <div class="order-item-amount text-mono" (click)="$event.stopPropagation()" (dblclick)="startEditingAmount(item)" title="Dwuklik zmienia ilość">
              @if (editingAmountItemId() === item.id) {
                <input #amountInput inputmode="decimal" [value]="formatAmountInput(item.data.amount)" (keyup.enter)="saveAmount(item, amountInput.value)" (keyup.escape)="stopEditingAmount()" (blur)="stopEditingAmount()"> {{ item.data.unit }}
              } @else {
                {{ formatAmount(item.data.amount, item.data.unit) }}
              }
            </div>
            @if (item.data.orderItemId && !item.data.clientOrder) {
              <button class="btn btn-small" (click)="$event.stopPropagation(); deleteItem(item)" title="Usuwa pozycję razem z jej drzewem">Usuń</button>
            } @else {
              <span></span>
            }
          </div>
        } @empty {
          <div class="positions-empty">Brak pozycji — dodaj pierwszą z katalogu BOM lub z zamówienia.</div>
        }
      </div>

      <div class="order-actions">
        <button class="btn" #dropdownTrigger>+ Nowa pozycja</button>
        <button class="btn" (click)="openPositionPicker($event)" title="Pozycja otwartego zamówienia, którą to zlecenie ma realizować">+ Z zamówienia</button>
      </div>

      <dropdown [searchFunc]="searchBomItems" [searchFormatter]="formatBomItem" [trigger]="dropdownTrigger"
        [minSearchLength]="0" [allowCreate]="true" [exactMatch]="isExactBomItemMatch" (itemSelected)="onNewItemSelected($event)"></dropdown>

      @if (state.positionPicker) {
        <client-position-picker [openAt]="state.positionPicker.position" (positionSelected)="onPositionSelected($event)" (closed)="onPositionPickerClose()"></client-position-picker>
      }
    }
  </div>

  <div class="card" id="summary-card">
    <div class="card-header">
      PODSUMOWANIE
      <!-- whose fold this is: the selected position's name, else the whole zlecenie (0097) -->
      <div class="card-header-scope" [title]="selectedItem(state)?.data?.name ?? 'całe zlecenie'">{{ selectedItem(state)?.data?.name ?? 'całe zlecenie' }}</div>
    </div>

    @if (state.loading) {
      <!-- three lines where the summary rows will stand (0127) -->
      <div class="shadow-line"><span class="shadow shadow-long"></span></div>
      <div class="shadow-line"><span class="shadow"></span></div>
      <div class="shadow-line"><span class="shadow shadow-short"></span></div>
    } @else if (state.order) {
      <tree-summary [items]="summaryItems(state)"></tree-summary>
    }
  </div>
}
